/**
 * Date-only arithmetic on 'YYYY-MM-DD' strings.
 *
 * Everything here works in UTC on purpose. A due date is a calendar fact, not a
 * moment: "follow-up due 6 April" must not shift because the server is in a
 * different zone or because British Summer Time started. Using Date objects with
 * local time is how compliance deadlines silently move by a day.
 */
export type IsoDate = string

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(value: unknown): value is IsoDate {
  return typeof value === 'string' && DATE_RE.test(value) && toUtc(value) !== null
}

function toUtc(date: IsoDate): number | null {
  const ms = Date.parse(`${date}T00:00:00Z`)
  if (Number.isNaN(ms)) return null
  // Rejects 2026-02-31, which parses but rolls over.
  return new Date(ms).toISOString().slice(0, 10) === date ? ms : null
}

function assertDate(date: IsoDate, label = 'date'): number {
  const ms = toUtc(date)
  if (ms === null) throw new Error(`Invalid ${label}: ${JSON.stringify(date)}`)
  return ms
}

const DAY_MS = 86_400_000

export function today(now: Date = new Date()): IsoDate {
  return now.toISOString().slice(0, 10)
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return new Date(assertDate(date) + days * DAY_MS).toISOString().slice(0, 10)
}

/** Positive when `to` is in the future relative to `from`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((assertDate(to, 'to') - assertDate(from, 'from')) / DAY_MS)
}

export function addMonths(date: IsoDate, months: number): IsoDate {
  const ms = assertDate(date)
  const d = new Date(ms)
  const targetMonth = d.getUTCMonth() + months
  const candidate = new Date(Date.UTC(d.getUTCFullYear(), targetMonth, d.getUTCDate()))
  // Clamp overflow: 31 January plus one month is 28 or 29 February, not 3 March.
  if (candidate.getUTCMonth() !== ((targetMonth % 12) + 12) % 12) {
    return new Date(Date.UTC(d.getUTCFullYear(), targetMonth + 1, 0)).toISOString().slice(0, 10)
  }
  return candidate.toISOString().slice(0, 10)
}

/** 0 = Sunday ... 6 = Saturday */
export function dayOfWeek(date: IsoDate): number {
  return new Date(assertDate(date)).getUTCDay()
}

export function isWeekend(date: IsoDate): boolean {
  const d = dayOfWeek(date)
  return d === 0 || d === 6
}

/**
 * Working-day arithmetic for the sponsor reporting deadlines, which are set in
 * working days rather than calendar days.
 *
 * Constructed with an explicit holiday set so the calculation is data-driven and
 * testable. An empty set degrades to weekends only, which is WRONG for a real
 * deadline, so callers are expected to load bank holidays and the loader warns
 * when the table is empty.
 */
export class WorkingDays {
  private readonly holidays: Set<IsoDate>

  constructor(holidays: Iterable<IsoDate> = []) {
    this.holidays = new Set(holidays)
  }

  get holidayCount(): number {
    return this.holidays.size
  }

  isWorkingDay(date: IsoDate): boolean {
    return !isWeekend(date) && !this.holidays.has(date)
  }

  /**
   * Adds `count` working days. Day zero is the trigger date itself, so
   * "ten working days from Monday" lands on the Monday a fortnight later,
   * matching how the Home Office counts.
   */
  add(date: IsoDate, count: number): IsoDate {
    assertDate(date)
    if (count === 0) return date
    const step = count > 0 ? 1 : -1
    let remaining = Math.abs(count)
    let cursor = date
    while (remaining > 0) {
      cursor = addDays(cursor, step)
      if (this.isWorkingDay(cursor)) remaining--
    }
    return cursor
  }

  /** Working days from `from` up to and including `to`, negative if `to` is earlier. */
  count(from: IsoDate, to: IsoDate): number {
    const total = daysBetween(from, to)
    const step = total >= 0 ? 1 : -1
    let cursor = from
    let n = 0
    for (let i = 0; i < Math.abs(total); i++) {
      cursor = addDays(cursor, step)
      if (this.isWorkingDay(cursor)) n += step
    }
    return n
  }
}
